import { Container, Graphics, Text, TextStyle, type Renderer, type RenderTexture } from 'pixi.js';
import { AsciiGenerator, CharsetPreset } from 'ts-ascii-engine';
import { BASE, LIGHT, rgb01 } from '../palette';
import { FONT_MONO } from '../theme';

/**
 * **ASCII art 那一档**（`FIELD_RENDER_SCALE < 0`）。
 *
 * 评审：
 *
 * > `FIELD_PIXEL_EXPONENT` 为负数时，改为使用 ASCII art 后处理，整个替换掉半调
 * > 后处理，且不经过像素化。每个 ASCII 字符只采样一次标量场，生成整个 ASCII art
 * > 后一起绘制到屏幕，负值越高，单个 ASCII 字符尺寸越大。
 *
 * 以及后来那句：
 *
 * > 此外 ASCIIart 请不要自己实现，去找一个现有的实现来用。
 *
 * ## 装配
 *
 * ```
 *   ① 场（着色器）  ──▶  cols × rows 的小 FBO    ← 一个纹素 = 一个字符的采样
 *   ② 读回来 ──▶ ImageData ──▶ ts-ascii-engine ──▶ 一整幅字符画的字符串
 *   ③ 一个 Pixi Text ──▶ 铺满整块视口
 * ```
 *
 * ⚠️ **换字符画那一步用的是 `ts-ascii-engine`**（零依赖，`AsciiGenerator`），
 *    自己那版手写的「着色器里查字形图集」删掉了：一来评审明确要求用现成的实现，
 *    二来那一版在 **DPR ≠ 1** 的机器上被 Pixi 的滤镜区域算法缩了一截
 *    （实测 DPR=2 时字符画只铺到屏幕的 76%，右边和下边各留一条黑边）。
 *    `Text` 是普通场景对象，任何 DPR / 窗口比例下都由渲染器自己缩放。
 *
 * ⚠️ 「每个字符只采样一次标量场」仍然成立：场就渲在 `列 × 行` 那么小的 FBO 上，
 *    每个字符对应**一个纹素**。1280×800 在 `-2` 档是 54×34 = 1836 个纹素，
 *    也就是场的着色器只跑 1836 次（全屏是 102 万次）。
 */

/** 字符梯度（暗 → 亮）。**第一个必须是空格**：场最暗的地方就该是空的 */
export const ASCII_RAMP = ' .:-=+*#%@';

/** 交给引擎的那条梯度（它收枚举或自定义串） */
export const ASCII_CHARSET: CharsetPreset | string = ASCII_RAMP;

/**
 * 读回 + 转字符画的**最小间隔**（毫秒）。
 *
 * 每帧做一次的话，一屏一两千个字符的 `fillText` 会把帧率拽下去；而场本身动得慢，
 * 15 Hz 完全够。**只在 ASCII 那一档才走这条路**（默认档位是 0，一次都不跑）。
 */
export const ASCII_CONVERT_MS = 66;

/**
 * 量字形用的字号（样式单位）。它只决定"量出来多少"，真实大小由 `scale` 换算 ——
 * 取大一点是为了量 `advance` 时少一点取整误差。
 */
const ASCII_FONT_SIZE = 64;

/**
 * **一台 ASCII art 画笔。**
 *
 * 它自己就是一个 `Container`（纸底 + 一整幅字符画），加进场景就完事。
 */
export class AsciiArt {
  /** 加进场景里的东西（铺满整块视口） */
  readonly view: Container;
  /** 像素 → 字符那一步的引擎。**列数/行数变了要重建**（见 `setLayout`） */
  #generator: AsciiGenerator;
  readonly #paper: Graphics;
  readonly #text: Text;
  /** 现在这一档的字符网格（列 × 行） */
  #cols = 0;
  #rows = 0;

  /** 距离上一次转换过了多少毫秒（走 `deltaMS` 累加，不用墙上时钟） */
  #sinceConvert = 0;
  /** 一个字符格现在多大（逻辑像素） */
  #cell = -1;
  /** 视口矩形（换了要重摆纸底和位置） */
  #quad: readonly [number, number, number, number] = [0, 0, 0, 0];
  /** 每帧的输入缓冲区（原地复用，不重新分配） */
  #rgba: Uint8ClampedArray<ArrayBuffer> | null = null;

  constructor() {
    this.view = new Container();
    this.view.label = 'bg-ascii';

    // 纸底：字符之外的地方也得是"纸"（不然会透出别的东西）
    this.#paper = new Graphics();
    this.#paper.label = 'bg-ascii-paper';
    this.view.addChild(this.#paper);

    this.#text = new Text({
      text: ' ',
      style: new TextStyle({
        fontFamily: FONT_MONO,
        fontSize: ASCII_FONT_SIZE,
        // ⚠️ 行高**必须**等于字号：字符格是方的，行距由我们自己算
        lineHeight: ASCII_FONT_SIZE,
        fill: LIGHT.halftone,
        letterSpacing: 0,
        padding: 0,
      }),
    });
    this.#text.label = 'bg-ascii-text';
    this.view.addChild(this.#text);

    // 引擎只做"像素 → 字符"这一段（这正是评审说的"别自己实现"）
    this.#generator = makeGenerator(1, 1);
  }

  /**
   * 摆好这一档的尺寸（视口换了、档位换回来都要调）。
   *
   * @param quad 视口在画布逻辑坐标里的矩形（`uQuad`）
   * @param cell 一个字符格的边长（逻辑像素）
   */
  setLayout(quad: readonly [number, number, number, number], cell: number): void {
    /**
     * ⚠️ **字符网格要显式告诉引擎。**
     *
     * 不给 `width` / `height` 时它会自己挑一个"大约 100 列"的目标
     * （`calculateDimensions` 的默认分支），于是 `-1` 档（107 列）会被它压成
     * 100 列 —— 场明明是一个字符一个纹素，画出来却少了几列。
     */
    const cols = Math.max(1, Math.ceil(quad[2] / cell));
    const rows = Math.max(1, Math.ceil(quad[3] / cell));
    if (cols !== this.#cols || rows !== this.#rows) {
      this.#cols = cols;
      this.#rows = rows;
      this.#generator = makeGenerator(cols, rows);
    }

    const sameQuad =
      this.#quad[0] === quad[0] &&
      this.#quad[1] === quad[1] &&
      this.#quad[2] === quad[2] &&
      this.#quad[3] === quad[3];
    if (sameQuad && this.#cell === cell) return;
    this.#quad = [quad[0], quad[1], quad[2], quad[3]];
    const resized = this.#cell !== cell;
    this.#cell = cell;

    this.#paper.clear();
    this.#paper
      .rect(quad[0], quad[1], quad[2], quad[3])
      .fill({ color: BASE.void });

    if (!resized) return;
    /**
     * 量一遍"一个字多宽、一行多高"，再把缩放和字距配到**正好一格**。
     *
     * ⚠️ 不能拿"字号 × 某个系数"去猜：等宽字体的 advance 和 line-height 都
     *    看字体实现，猜错的表现是最后一行/一列被挤出屏幕。
     */
    const probe = new Text({
      text: ASCII_RAMP,
      style: new TextStyle({
        fontFamily: FONT_MONO,
        fontSize: ASCII_FONT_SIZE,
        lineHeight: ASCII_FONT_SIZE,
        letterSpacing: 0,
        padding: 0,
      }),
    });
    const advance = Math.max(1, probe.width / ASCII_RAMP.length);
    probe.destroy();

    /**
     * ⚠️ 缩放用 **`lineHeight`（样式里定死的那个数）**，不用 `probe.height`：
     *    多行文本的行距是 `lineHeight × scale`，而 Pixi 量出来的单行高度
     *    带有取整/内边距，比 `lineHeight` 差几个像素 —— 拿它当行高的话
     *    34 行会少铺几十个像素（右边铺满了、下边还差一截）。
     */
    this.#text.scale.set(this.#cell / ASCII_FONT_SIZE);
    // 让"一个字"的宽度也正好是一格：多退少补进字距（样式单位，会跟着 scale 缩）
    this.#text.style.letterSpacing = (this.#cell - advance * this.#text.scale.x) / this.#text.scale.x;
    this.#text.position.set(quad[0], quad[1]);
  }

  /**
   * 换一帧的字符画。**只在 ASCII 那一档调它。**
   *
   * @param field 场那张"一字符一纹素"的小图
   * @param tint 情绪色调（和半调那一级同一个口径）
   * @param deltaMS 这一帧多长（节流用）
   */
  update(
    renderer: Renderer,
    field: RenderTexture,
    tint: readonly [number, number, number],
    deltaMS: number,
  ): void {
    this.#sinceConvert += deltaMS;
    if (this.#sinceConvert < ASCII_CONVERT_MS) return;
    this.#sinceConvert = 0;

    const image = renderer.extract.pixels(field);
    const need = image.width * image.height * 4;
    if (this.#rgba === null || this.#rgba.length !== need) {
      // ⚠️ 显式要一个 `ArrayBuffer`：`ImageData` 的构造函数不收
      //    `ArrayBufferLike`（类型上可能是 SharedArrayBuffer），TS 会拦
      this.#rgba = new Uint8ClampedArray(new ArrayBuffer(need));
    }
    this.#rgba.set(image.pixels as unknown as Uint8ClampedArray);
    /**
     * ⚠️ `ImageData` 是浏览器的全局对象（`src/render/**` 里不许碰 `document`，
     *    但这个是数据容器，不是 DOM 操作）。引擎见到 `ImageData` 会**直接取
     *    数据**，不会去建 canvas —— 见 `ts-ascii-engine` 的 `extractPixelData`。
     */
    const data = new ImageData(this.#rgba, image.width, image.height);
    this.#text.text = this.#generator.convertImage(data).text;
    // 情绪：色调乘在墨色上（和半调那一级一致 —— ASCII 档也不该"没有情绪"）
    this.#text.style.fill = tinted(LIGHT.halftone, tint);
  }

  /** 现在这一档的字符网格（调试与测试用） */
  get grid(): { readonly cols: number; readonly rows: number } {
    return { cols: this.#cols, rows: this.#rows };
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}

/** 造一台引擎。**宽高必须显式给**（见 `setLayout` 里那段说明） */
function makeGenerator(cols: number, rows: number): AsciiGenerator {
  return new AsciiGenerator({
    charset: ASCII_CHARSET,
    // ⚠️ 格子的宽高比由**我们**定（一个字符一格），不要它按字体习惯纠正
    aspectRatio: 1,
    width: cols,
    height: rows,
    optimized: true,
  });
}

/** 把 `[r,g,b]` 的色调倍率乘到一个色值上（和着色器里 `color * uMoodTint` 同义） */
function tinted(hex: number, tint: readonly [number, number, number]): number {
  const [r, g, b] = rgb01(hex);
  const byte = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
  return (
    (byte(r * (tint[0] ?? 1)) << 16) | (byte(g * (tint[1] ?? 1)) << 8) | byte(b * (tint[2] ?? 1))
  );
}
